import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, ShieldX, Clock, AlertCircle, CheckCircle, LucideIcon } from 'lucide-react';
import { isAuthenticated, getAccountHealth, AccountHealth, MemberViolation } from '../services/api';

const HEALTH_CONFIG: Record<string, {
  label: string;
  description: string;
  gaugeColor: string;
  textColor: string;
  bgColor: string;
  Icon: LucideIcon;
}> = {
  Excellent: {
    label: 'Excellent', description: 'Your account is in great standing. Keep it up!',
    gaugeColor: '#22c55e', textColor: 'text-green-700', bgColor: 'bg-green-50',
    Icon: ShieldCheck
  },
  Good: {
    label: 'Good', description: 'Your account is in good standing.',
    gaugeColor: '#3b82f6', textColor: 'text-blue-700', bgColor: 'bg-blue-50',
    Icon: ShieldCheck
  },
  Fair: {
    label: 'Fair', description: 'You have some violations on record. Please review the policies below.',
    gaugeColor: '#eab308', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50',
    Icon: ShieldAlert
  },
  Poor: {
    label: 'Poor', description: 'Your account has multiple violations. Further violations may result in suspension.',
    gaugeColor: '#f97316', textColor: 'text-orange-700', bgColor: 'bg-orange-50',
    Icon: ShieldAlert
  },
  Critical: {
    label: 'Critical', description: 'Your account is at risk. Please visit a store location to discuss your standing.',
    gaugeColor: '#ef4444', textColor: 'text-red-700', bgColor: 'bg-red-50',
    Icon: ShieldX
  },
  Suspended: {
    label: 'Suspended', description: 'Your account is temporarily suspended. Rewards cannot be redeemed during this period.',
    gaugeColor: '#b91c1c', textColor: 'text-red-900', bgColor: 'bg-red-100',
    Icon: ShieldX
  },
  Banned: {
    label: 'Banned', description: 'Your account has been permanently suspended. Please visit a BuggyBusters location for assistance.',
    gaugeColor: '#111827', textColor: 'text-gray-900', bgColor: 'bg-gray-200',
    Icon: ShieldX
  },
};

const CONSEQUENCE_COLORS: Record<string, string> = {
  Warning:    'bg-yellow-100 text-yellow-800',
  Suspension: 'bg-orange-100 text-orange-800',
  Ban:        'bg-red-100 text-red-800',
};

// SVG semicircle gauge
const HealthGauge: React.FC<{ score: number; color: string }> = ({ score, color }) => {
  const r = 70;
  const cx = 90;
  const cy = 90;
  const circumference = Math.PI * r; // half circle
  const progress = (score / 100) * circumference;

  return (
    <svg viewBox="0 0 180 100" className="w-48 mx-auto">
      {/* Track */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none" stroke="#e5e7eb" strokeWidth="12" strokeLinecap="round"
      />
      {/* Progress */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
        strokeDasharray={`${progress} ${circumference}`}
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      {/* Score text */}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="24" fontWeight="bold" fill={color}>{score}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" fill="#6b7280">out of 100</text>
    </svg>
  );
};

const ViolationCard: React.FC<{ violation: MemberViolation }> = ({ violation }) => {
  const isActive = violation.status === 'Active';
  const expiresDate = new Date(violation.expiresDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const issuedDate = new Date(violation.issuedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className={`rounded-xl border p-4 ${isActive ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-bold text-buggy-dark">{violation.violationName}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CONSEQUENCE_COLORS[violation.consequenceType] || 'bg-gray-100 text-gray-700'}`}>
              {violation.consequenceType}
            </span>
            {!isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 flex items-center gap-1">
                <CheckCircle size={10} /> {violation.status}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 mb-2">{violation.violationDescription}</p>
          {violation.notes && (
            <p className="text-sm text-gray-600 italic bg-white rounded px-3 py-2 border border-gray-200 mb-2">
              Staff note: "{violation.notes}"
            </p>
          )}
        </div>
        <div className={`text-sm font-bold flex-shrink-0 ${isActive ? 'text-red-600' : 'text-gray-400'}`}>
          {violation.healthScoreImpact} pts
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><Clock size={11} /> Issued {issuedDate}</span>
        {isActive && <span>· Expires {expiresDate}</span>}
      </div>
    </div>
  );
};

const AccountHealthPage: React.FC = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState<AccountHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    loadHealth();
  }, []);

  const loadHealth = async () => {
    try {
      const h = await getAccountHealth();
      setHealth(h);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4" />
          <p className="text-buggy-gray">Loading account health...</p>
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertCircle size={40} className="text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">{error || 'Could not load account health.'}</p>
        </div>
      </div>
    );
  }

  const config = HEALTH_CONFIG[health.status] || HEALTH_CONFIG['Good'];
  const { Icon } = config;
  const activeViolations = health.violations.filter(v => v.status === 'Active');
  const pastViolations   = health.violations.filter(v => v.status !== 'Active');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Account Health</h1>
              <p className="text-sm opacity-90">Your policy standing at BuggyBusters</p>
            </div>
            <div className={`flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 shadow-sm`}>
              <Icon size={16} strokeWidth={2.5} className={config.textColor} />
              <span className={`text-xs font-semibold ${config.textColor}`}>{config.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="max-w-md mx-auto p-4 space-y-4">

          {/* Suspended / Banned Banner */}
          {(health.isSuspended || health.isBanned) && (
            <div className="bg-red-600 text-white rounded-xl p-5">
              <div className="flex items-start gap-3">
                <ShieldX size={24} className="flex-shrink-0 mt-0.5" />
                <div>
                  {health.isBanned ? (
                    <>
                      <p className="font-bold text-lg">Account Permanently Suspended</p>
                      <p className="text-sm text-red-100 mt-1">You are unable to earn points or redeem rewards. Please visit any BuggyBusters location for assistance.</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-lg">Account Temporarily Suspended</p>
                      {health.suspendedUntil && (
                        <p className="text-sm text-red-100 mt-1">
                          Your account is suspended until {new Date(health.suspendedUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Rewards cannot be redeemed during this time.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Health Score Card */}
          <div className={`${config.bgColor} rounded-xl p-5`}>
            <HealthGauge score={health.score} color={config.gaugeColor} />
            <div className="text-center mt-3">
              <p className={`text-lg font-bold ${config.textColor}`}>{config.label}</p>
              <p className="text-sm text-gray-600 mt-1">{config.description}</p>
            </div>
          </div>

          {/* Active Violations */}
          {activeViolations.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Active Violations</h2>
              <div className="space-y-3">
                {activeViolations.map(v => <ViolationCard key={v.violationId} violation={v} />)}
              </div>
            </div>
          )}

          {/* What Affects Your Score */}
          <div className="bg-white rounded-xl shadow-md p-5">
            <h2 className="font-bold text-buggy-dark mb-3">How Account Health Works</h2>
            <div className="space-y-2 text-sm text-gray-600">
              <p>Your account starts at <span className="font-semibold">100 (Excellent)</span>. Violations reduce your score based on their severity.</p>
              <p>Violations typically expire after <span className="font-semibold">180 days</span>, at which point your score recovers.</p>
              <p>If you believe a violation was issued in error, please visit any <span className="font-semibold">BuggyBusters</span> location and speak with a manager.</p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              {[
                { range: '80–100', label: 'Excellent', color: 'bg-green-100 text-green-800' },
                { range: '60–79',  label: 'Good',      color: 'bg-blue-100 text-blue-800' },
                { range: '40–59',  label: 'Fair',      color: 'bg-yellow-100 text-yellow-800' },
                { range: '20–39',  label: 'Poor',      color: 'bg-orange-100 text-orange-800' },
                { range: '0–19',   label: 'Critical',  color: 'bg-red-100 text-red-800' },
              ].map(t => (
                <div key={t.range} className={`flex items-center justify-between px-3 py-2 rounded-lg ${t.color}`}>
                  <span className="font-semibold">{t.label}</span>
                  <span>{t.range}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Past Violations */}
          {pastViolations.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Past Violations</h2>
              <div className="space-y-3">
                {pastViolations.map(v => <ViolationCard key={v.violationId} violation={v} />)}
              </div>
            </div>
          )}

          {/* All clear */}
          {health.violations.length === 0 && (
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <ShieldCheck size={40} className="text-green-500 mx-auto mb-3" />
              <p className="font-bold text-buggy-dark">No violations on record</p>
              <p className="text-sm text-buggy-gray mt-1">Keep up the great behavior!</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AccountHealthPage;
